import React, { useState, useEffect } from "react";
import TodoItem from "./TodoItem";
import "./index.css";

export default function App() {
  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem("todos");
    return saved ? JSON.parse(saved) : [];
  });
  const [newTask, setNewTask] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");

  useEffect(() => {
    localStorage.setItem("todos", JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  // 🔔 Check for reminders every minute
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().toISOString().slice(0, 16);
      tasks.forEach(task => {
        if (task.dueDate && !task.completed && task.dueDate <= now) {
          alert(`⏰ Reminder: "${task.text}" is due!`);
        }
      });
    }, 60000);
    return () => clearInterval(interval);
  }, [tasks]);

  const addTask = () => {
    if (!newTask.trim()) return;
    setTasks([
      ...tasks,
      {
        id: Date.now(),
        text: newTask,
        completed: false,
        dueDate: dueDate || null,
        priority: ""
      }
    ]);
    setNewTask("");
    setDueDate("");
  };

  const toggleComplete = (id) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  };

  const editTask = (id, newText) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, text: newText } : t));
  };

  const updateDueDate = (id, newDate) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, dueDate: newDate } : t));
  };

  const updatePriority = (id, newPriority) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, priority: newPriority } : t));
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(t => t.id !== id));
  };

  const clearCompleted = () => {
    setTasks(tasks.filter(t => !t.completed));
  };

  const filteredTasks = tasks
    .filter(task => {
      if (filter === "completed") return task.completed;
      if (filter === "pending") return !task.completed;
      return true;
    })
    .filter(task => task.text.toLowerCase().includes(search.toLowerCase()));

  const totalTasks = tasks.length;
  const completedCount = tasks.filter(t => t.completed).length;
  const pendingCount = totalTasks - completedCount;

  return (
    <div className={`app ${darkMode ? "dark" : ""}`}>
      <div className="header">
        <h1>Todo List</h1>
        <button className="toggle-theme" onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
        </button>
      </div>

      <div className="input-section">
        <input
          type="text"
          value={newTask}
          placeholder="Enter a task..."
          onChange={(e) => setNewTask(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTask()}
        />
        <input
          type="datetime-local"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        <button onClick={addTask}>Add</button>
      </div>

      <div className="search-section">
        <input
          type="text"
          value={search}
          placeholder="🔍 Search tasks..."
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="filters">
        <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>All</button>
        <button className={filter === "completed" ? "active" : ""} onClick={() => setFilter("completed")}>Completed</button>
        <button className={filter === "pending" ? "active" : ""} onClick={() => setFilter("pending")}>Pending</button>
      </div>

      <div className="task-list">
        {filteredTasks.length === 0 ? (
          <p className="empty">No tasks found.</p>
        ) : (
          filteredTasks.map(task => (
            <TodoItem
              key={task.id}
              task={task}
              toggleComplete={toggleComplete}
              editTask={editTask}
              deleteTask={deleteTask}
              updateDueDate={updateDueDate}
              updatePriority={updatePriority}
            />
          ))
        )}
      </div>

      <div className="footer">
        <p>📌 Total: {totalTasks} | ⏳ Pending: {pendingCount} | ✅ Completed: {completedCount}</p>
        {tasks.some(task => task.completed) && (
          <button onClick={clearCompleted} className="clear-btn">Clear Completed</button>
        )}
      </div>
    </div>
  );
}
